import { useState, useMemo, useEffect } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import moment from "moment";
import { getCameraId } from "../../../shared/utils/utils";
import { getAzureBlob, saveContainerWeight } from "../../../services/container";
import {
  buildCharacterizationCommentsBlobInfo,
  getCommentAuthorName,
  getImageUrlFromHistory,
  loadCharacterizationComments,
  parseCharacterizationComments,
} from "../core/utils/characterizationComments";
import InfoCardGrid from "./InfoCardGrid";
import { ImageCardComponent } from "../../../components/imageCard/ImageCardComponent";
import {
  buildPeriodUpdatesFromClientForm,
  sortByImageUrl,
} from "../core/utils/utils";
import { useContainerClientInfo } from "../core/hooks/useContainerClientInfo";
import { useClientData } from "../core/hooks/useClientData";
import EditClientDataModal from "./EditClientDataModal";
import CommentModal from "./CommentModal";
import DangerAlert from "./DangerAlert";
import EmptyState from "../../../components/EmptyState";
import {
  getChar2AlertShown,
  setChar2AlertShown,
  CHAR2_ALERT_IDS,
} from "../core/utils/alertOncePerSession";
import { useTranslation } from "react-i18next";
import { formatCommentDate } from "../core/utils/dateFormatUtils";

const CharacterizationReview = ({
  period,
  container,
  superContainer,
  characterizationInProgress = false,
  historyMaterial,
  historyId,
  setHistoryId,
  containers,
  proportion = {},
  onAfterSegmentationUpdate,
  isPageLoading = false,
  onCommentsChange,
  onPeriodUpdate,
  hasSizeRanges = false,
}) => {
  const { t, i18n } = useTranslation();
  const [displayModal, setDisplayModal] = useState(false);
  const [isModalDataLoading, setIsModalDataLoading] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState([]);
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isDangerAlertVisible, setIsDangerAlertVisible] = useState(true);
  const [editingCommentIndex, setEditingCommentIndex] = useState(null);
  const [openCommentMenuIndex, setOpenCommentMenuIndex] = useState(null);
  const [commentIndexToDelete, setCommentIndexToDelete] = useState(null);

  const updateComments = (nextComments) => {
    setComments(nextComments);
    onCommentsChange?.(nextComments);
  };

  const {
    clientInfo: containerClientInfo,
    isLoading: isClientInfoLoading,
    refetch: refetchClientInfo,
  } = useContainerClientInfo({
    container,
    superContainer,
    historyMaterial,
    historyId: 0,
    period,
  });

  const {
    clientName,
    clientId,
    clientCode,
    clientAddress,
    weight,
    wasteCategoryName,
    clientList,
    wasteCategoryList,
    isLoading: isClientDataLoading,
    isSaving,
    fetchClients,
    fetchWasteCategories,
    loadClientData,
    saveClientData,
  } = useClientData({
    container,
    superContainer,
    historyMaterial,
    historyId,
    period,
  });

  useEffect(() => {
    if (!container?._id) return;
    fetchClients().catch(() => {});
    fetchWasteCategories().catch(() => {});
  }, [container?._id, fetchClients, fetchWasteCategories]);

  useEffect(() => {
    if (openCommentMenuIndex === null) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest(".char-comment-item-actions-menu")) {
        setOpenCommentMenuIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openCommentMenuIndex]);

  const handleOpenEditModal = () => {
    setDisplayModal(true);
    setIsModalDataLoading(true);
    (async () => {
      try {
        const [clients] = await Promise.all([
          fetchClients(),
          fetchWasteCategories(),
        ]);
        await loadClientData(Array.isArray(clients) ? clients : null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error preloading client data for modal:", err);
      } finally {
        setIsModalDataLoading(false);
      }
    })();
  };

  const handleSave = async (formValues) => {
    const success = await saveClientData(formValues);
    if (success) {
      setDisplayModal(false);
      refetchClientInfo();
      onPeriodUpdate?.(buildPeriodUpdatesFromClientForm(formValues));
    }
  };

  // Load comments
  useEffect(
    () => {
      const loadComments = async () => {
        try {
          const loadedComments = await loadCharacterizationComments({
            container,
            superContainer,
            historyMaterial,
            historyId,
          });
          updateComments(loadedComments);
        } catch (error) {
          console.error("Error loading comments:", error);
          updateComments([]);
        }
      };

      loadComments();
    },
    // eslint-disable-next-line
    [container?._id, superContainer?._id, historyMaterial, historyId],
  );

  // Utility functions for comment handling
  const getUserFromStorage = () => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  };

  const parseExistingComments = parseCharacterizationComments;

  const buildBlobInfo = (cameraId, timeStamp, containerId) =>
    buildCharacterizationCommentsBlobInfo({ cameraId, timeStamp, containerId });

  const persistComments = async (commentsList) => {
    const targetHistory = historyMaterial?.[historyId] || historyMaterial?.[0];
    const timeStamp =
      targetHistory?._time ||
      new Date().toISOString().replace(/:/g, "_").slice(0, -1);
    const imageUrl =
      getImageUrlFromHistory(targetHistory) || container?.image_url || "";
    const cameraId = getCameraId(imageUrl);

    if (!cameraId || !timeStamp || !container?._id) {
      return false;
    }

    const containerId = superContainer?._id || container._id;
    const { blobName, blobUrl } = buildBlobInfo(
      cameraId,
      timeStamp,
      containerId,
    );

    setIsSavingComment(true);
    try {
      let existing = {};
      try {
        const azureResponse = await getAzureBlob({ blobUrl });
        existing = azureResponse.data || {};
      } catch (err) {
        existing = {};
      }

      const payload = {
        ...existing,
        id: containerId,
        time: timeStamp,
        modified_time: moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        blobName,
        comments: JSON.stringify(commentsList),
      };

      await saveContainerWeight(payload);
      updateComments(commentsList);
      return true;
    } catch (err) {
      console.error("Error saving comment", err);
      return false;
    } finally {
      setIsSavingComment(false);
    }
  };

  const handleSaveComment = async () => {
    const targetHistory = historyMaterial?.[historyId] || historyMaterial?.[0];
    const timeStamp =
      targetHistory?._time ||
      new Date().toISOString().replace(/:/g, "_").slice(0, -1);
    const imageUrl =
      getImageUrlFromHistory(targetHistory) || container?.image_url || "";
    const cameraId = getCameraId(imageUrl);

    if (!cameraId || !timeStamp || !container?._id) {
      setNewComment("");
      setIsCommentModalOpen(false);
      setEditingCommentIndex(null);
      return;
    }

    const containerId = superContainer?._id || container._id;
    const { blobUrl } = buildBlobInfo(cameraId, timeStamp, containerId);
    const user = getUserFromStorage();

    let existingComments = comments;
    try {
      const azureResponse = await getAzureBlob({ blobUrl });
      existingComments = parseExistingComments(azureResponse.data?.comments);
    } catch (err) {
      // Utilise la liste locale si le blob n'existe pas encore
    }

    let commentsList;
    if (editingCommentIndex !== null) {
      commentsList = [...existingComments];
      commentsList[editingCommentIndex] = {
        ...commentsList[editingCommentIndex],
        text: newComment.trim(),
        modified_time: new Date().toISOString(),
      };
    } else {
      const commentToAdd = {
        time: new Date().toISOString(),
        author: user,
        text: newComment.trim(),
      };
      commentsList = [...existingComments, commentToAdd];
    }

    const success = await persistComments(commentsList);
    if (success) {
      setNewComment("");
      setIsCommentModalOpen(false);
      setEditingCommentIndex(null);
    }
  };

  const requestDeleteComment = (index) => {
    setOpenCommentMenuIndex(null);
    setCommentIndexToDelete(index);
  };

  const confirmDeleteComment = async () => {
    if (commentIndexToDelete === null) return;

    const commentsList = comments.filter((_, i) => i !== commentIndexToDelete);
    const success = await persistComments(commentsList);
    if (success) {
      setNewComment("");
      setIsCommentModalOpen(false);
      setEditingCommentIndex(null);
      setCommentIndexToDelete(null);
    }
  };

  const handleEditComment = (index) => {
    const comment = comments[index];
    setNewComment(comment.text || "");
    setEditingCommentIndex(index);
    setOpenCommentMenuIndex(null);
    setIsCommentModalOpen(true);
  };

  const handleAddComment = () => {
    setNewComment("");
    setEditingCommentIndex(null);
    setIsCommentModalOpen(true);
  };

  const filteredContainers = useMemo(
    () =>
      containers
        .filter((item) =>
          superContainer?.containerObjectIdList.includes(item._id),
        )
        .sort(sortByImageUrl),
    [containers, superContainer?.containerObjectIdList],
  );

  // ImageCardComponent/CropImage attend container._measurement pour le blobName (deleteSegmentation).
  // En characterization-2 les conteneurs viennent de MongoDB et n'ont que _id : on normalise pour
  // que _measurement soit défini (comme en characterisation v1 où l'API peut renvoyer les deux).
  const containerForImageCard = useMemo(
    () =>
      container
        ? {
            ...container,
            _measurement: container._measurement ?? container._id,
          }
        : null,
    [container],
  );
  const filteredContainersForImageCard = useMemo(
    () =>
      (filteredContainers || []).map((c) => ({
        ...c,
        _measurement: c._measurement ?? c._id,
      })),
    [filteredContainers],
  );

  // Detect dangerous waste based on anomaliesAnnotation
  // Only consider anomalies with cls === "hazardous" as dangerous
  const hasDangerousWaste = useMemo(() => {
    if (!historyMaterial || historyMaterial.length === 0) return false;
    for (const history of historyMaterial) {
      if (
        history.anomaliesAnnotation &&
        Array.isArray(history.anomaliesAnnotation)
      ) {
        const hasHazardousMaterial = history.anomaliesAnnotation.some(
          (anomaly) =>
            anomaly && anomaly.cls && anomaly.cls.toLowerCase() === "hazardous",
        );
        if (hasHazardousMaterial) return true;
      }
    }
    return false;
  }, [historyMaterial]);

  return (
    <div>
      <div className="char-section-header char-section-header--general-info flex flex-row flex-wrap gap-3 justify-between">
        <h2 className="char-section-header-title">
          {t("General Information")}
        </h2>
        <div className="char-section-header-actions flex justify-start">
          <button
            type="button"
            onClick={handleOpenEditModal}
            className="char-button char-button-primary"
            disabled={isPageLoading || isClientInfoLoading}
          >
            <span>{t("Edit general information")}</span>
          </button>
        </div>
      </div>
      <InfoCardGrid data={containerClientInfo} />
      <div className="mt-8 relative z-10 isolate">
        <div className="char-section-header char-section-header--waste-ai flex flex-row flex-wrap gap-3 justify-between">
          <h2 className="char-section-header-title">
            {t("Waste analyzed by AI")}
          </h2>
        </div>
        {characterizationInProgress && (
          <div className="char-card mb-6 px-4 py-3 text-center">
            <h6 className="font-bold">
              {t("Photo recieved and being analysed by IA.")}
            </h6>
          </div>
        )}
        {hasDangerousWaste &&
          isDangerAlertVisible &&
          !getChar2AlertShown(CHAR2_ALERT_IDS.DANGER_WASTE) && (
            <div className="mb-6">
              <DangerAlert
                onClose={() => {
                  setIsDangerAlertVisible(false);
                  setChar2AlertShown(CHAR2_ALERT_IDS.DANGER_WASTE);
                }}
              />
            </div>
          )}
        <div className="char-card p-3">
          {historyMaterial.length > 0 &&
            (!superContainer || filteredContainers.length > 0) && (
              <ImageCardComponent
                container={containerForImageCard}
                displayLegend={false}
                history={historyMaterial}
                historyId={historyId}
                setHistoryId={setHistoryId}
                isCharacterization={true}
                containers={filteredContainersForImageCard}
                superContainer={superContainer}
                proportion={proportion}
                displayAllCountours={true}
                hideMaterials={characterizationInProgress}
                hideShowNewObjectsButton={true}
                hideSizeRangeDropdown={!hasSizeRanges}
                onAfterSegmentationUpdate={onAfterSegmentationUpdate}
              />
            )}
        </div>
      </div>
      <div className="mt-8">
        <div className="char-section-header flex flex-row flex-wrap gap-3 justify-between">
          <h2 className="char-section-header-title">
            {t("Comments")} ({comments.length})
          </h2>
          {comments.length > 0 && (
            <div className="char-section-header-actions flex justify-start">
              <button
                type="button"
                onClick={handleAddComment}
                className="char-button char-button-primary"
                style={{ fontWeight: 400 }}
              >
                <span>{t("Add a comment")}</span>
              </button>
            </div>
          )}
        </div>
        {comments.length === 0 ? (
          <EmptyState
            className="char-comments-empty-state"
            title={t("No comments for the moment.")}
            description={t(
              "Share your observations to enrich the characterization.",
            )}
            buttonText={t("Add a comment")}
            onButtonClick={handleAddComment}
          />
        ) : (
          <div className="char-card divide-y divide-[#e9e9e9]">
            {comments.map((comment, index) => {
              const commentDate = comment.time
                ? new Date(comment.time)
                : new Date();
              const author = comment.author || {};
              const authorName = getCommentAuthorName(author) || t("Unknown");

              return (
                <div key={index} className="char-comment-item">
                  <div className="char-comment-item-header">
                    <span className="char-comment-item-header-date">
                      {formatCommentDate(commentDate, i18n.language)}
                    </span>
                    <div className="char-comment-item-actions-menu table-standard-row-actions-menu-anchor relative">
                      <button
                        type="button"
                        className="table-standard-row-actions-dots"
                        disabled={isSavingComment}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCommentMenuIndex((current) =>
                            current === index ? null : index,
                          );
                        }}
                        aria-label={t("Actions")}
                        aria-expanded={openCommentMenuIndex === index}
                      >
                        <i
                          className={`fas ${openCommentMenuIndex === index ? "fa-times" : "fa-ellipsis-v"}`}
                        />
                      </button>
                      <div
                        className={`action-choice ${openCommentMenuIndex === index ? "display-action" : ""}`}
                      >
                        <button
                          type="button"
                          className="action"
                          disabled={isSavingComment}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditComment(index);
                          }}
                        >
                          {t("Edit")}
                        </button>
                        <button
                          type="button"
                          className="action"
                          disabled={isSavingComment}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestDeleteComment(index);
                          }}
                        >
                          {t("Delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                  <p className="char-comment-item-content">{comment.text}</p>
                  <div className="char-comment-item-footer">
                    <div className="char-comment-item-footer-avatar">
                      {authorName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase() || "?"}
                    </div>
                    <span className="char-comment-item-footer-author">
                      {authorName}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <EditClientDataModal
        isOpen={displayModal}
        onClose={() => {
          setDisplayModal(false);
          setIsModalDataLoading(false);
        }}
        onSave={handleSave}
        isDataLoading={isClientDataLoading || isModalDataLoading}
        isLoading={isSaving}
        clientName={clientName}
        clientId={clientId}
        clientCode={clientCode}
        clientAddress={clientAddress}
        clientList={clientList}
        weight={weight}
        wasteCategoryName={wasteCategoryName}
        wasteCategoryList={wasteCategoryList}
      />
      <CommentModal
        isOpen={isCommentModalOpen}
        onClose={() => {
          setIsCommentModalOpen(false);
          setEditingCommentIndex(null);
          setNewComment("");
        }}
        onSave={handleSaveComment}
        isLoading={isSavingComment}
        comment={newComment}
        setComment={setNewComment}
        isEditing={editingCommentIndex !== null}
      />
      <Modal
        isOpen={commentIndexToDelete !== null}
        toggle={() => !isSavingComment && setCommentIndexToDelete(null)}
        contentClassName="rounded-lg"
        style={{ maxWidth: "520px" }}
        centered
      >
        <ModalHeader className="flex justify-between items-center pb-3 border-0">
          <span className="text-gray-900 font-semibold text-base">
            {t("Delete")}
          </span>
          <CloseIcon
            style={{
              color: "#6B7280",
              cursor: "pointer",
              fontSize: "20px",
            }}
            onClick={() => !isSavingComment && setCommentIndexToDelete(null)}
          />
        </ModalHeader>
        <ModalBody className="px-6 pt-0">
          <p className="text-sm text-gray-700 mb-0">
            {t("Are you sure you want to delete this comment?")}
          </p>
        </ModalBody>
        <ModalFooter className="flex justify-end gap-2 pt-4 pb-4 border-0 px-6">
          <button
            type="button"
            onClick={() => setCommentIndexToDelete(null)}
            className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 text-sm font-medium"
            disabled={isSavingComment}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            onClick={confirmDeleteComment}
            disabled={isSavingComment}
            className="px-4 py-2 bg-[#AF2F3C] text-white rounded-full hover:bg-[#962832] disabled:opacity-50 text-sm font-medium"
          >
            {isSavingComment ? t("Loading...") : t("Confirm")}
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};
export default CharacterizationReview;
